import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";

/**
 * Compact "used/limit" indicator for the MOST CRITICAL daily bucket (highest %).
 * - No new endpoint: consumes /api/usage/limits.
 * - Loads on mount + soft re-fetch on route change (no polling).
 * - Hides itself if the request fails (does not break navigation).
 * - Falls back to neutral "∞" when no bucket has a limit configured.
 */
const BUCKET_LABEL = {
  analyses: { es: "análisis", en: "analyses" },
  compare: { es: "comparativos", en: "compare" },
  briefing: { es: "PDFs", en: "PDFs" },
  alerts: { es: "emails", en: "emails" },
};

export default function UsageBadge({ variant = "default" }) {
  const { lang } = useLang();
  const location = useLocation();
  const [buckets, setBuckets] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/usage/limits")
      .then((r) => {
        if (cancelled) return;
        setBuckets(r.data?.limits || []);
      })
      .catch(() => {
        if (!cancelled) setHidden(true);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const dominant = useMemo(() => {
    if (!buckets || buckets.length === 0) return null;
    // Only buckets with a positive configured limit are candidates
    const candidates = buckets.filter(
      (b) => typeof b.limit_daily === "number" && b.limit_daily > 0,
    );
    if (candidates.length === 0) return null;
    let best = candidates[0];
    let bestPct = (best.used_today ?? 0) / best.limit_daily;
    for (const b of candidates.slice(1)) {
      const p = (b.used_today ?? 0) / b.limit_daily;
      if (p > bestPct) {
        best = b;
        bestPct = p;
      }
    }
    return best;
  }, [buckets]);

  if (hidden) return null;

  const isInverted = variant === "inverted";
  const baseClasses =
    "px-2.5 py-1.5 border font-mono-tech text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 transition-colors";

  // No bucket has a configured limit → neutral ∞ pill
  if (!dominant) {
    if (!buckets) return null; // still loading
    return (
      <Link
        to="/app/usage"
        data-testid="usage-badge"
        data-tone="normal"
        title={lang === "es" ? "Sin límites configurados" : "No limits configured"}
        className={`${baseClasses} ${
          isInverted
            ? "border-white/40 text-white/80 hover:border-white hover:text-white"
            : "border-[#0A0A0A]/30 text-[#404040] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
        }`}
      >
        <span
          className={`w-1.5 h-1.5 ${isInverted ? "bg-white/60" : "bg-[#0A0A0A]"}`}
        />
        <span data-testid="usage-badge-value">∞</span>
      </Link>
    );
  }

  const used = dominant.used_today ?? 0;
  const limit = dominant.limit_daily;
  const pct = (used / limit) * 100;

  let tone = "normal";
  if (used >= limit) tone = "critical";
  else if (pct >= 80) tone = "warn";

  const toneClasses = {
    normal: isInverted
      ? "border-white/40 text-white/80 hover:border-white hover:text-white"
      : "border-[#0A0A0A]/30 text-[#404040] hover:border-[#0A0A0A] hover:text-[#0A0A0A]",
    warn: "border-[#D90429] text-[#D90429] hover:bg-[#D90429] hover:text-white",
    critical:
      "border-[#D90429] bg-[#D90429] text-white hover:bg-[#0A0A0A] hover:border-[#0A0A0A]",
  };

  const label = BUCKET_LABEL[dominant.bucket]?.[lang] ?? dominant.bucket;
  const title =
    lang === "es"
      ? `Uso de hoy · ${used} de ${limit} ${label}`
      : `Today's usage · ${used} of ${limit} ${label}`;

  return (
    <Link
      to="/app/usage"
      data-testid="usage-badge"
      data-tone={tone}
      data-bucket={dominant.bucket}
      title={title}
      className={`${baseClasses} ${toneClasses[tone]}`}
    >
      <span
        className={`w-1.5 h-1.5 ${
          tone === "normal" ? (isInverted ? "bg-white/60" : "bg-[#0A0A0A]") : "bg-current"
        }`}
      />
      <span data-testid="usage-badge-value">
        {used}/{limit}
      </span>
      <span className="opacity-70 normal-case tracking-normal hidden lg:inline">
        {label}
      </span>
    </Link>
  );
}
